<template>
  <div class="dpv-table table-component">
    <artisio-table :pagination="tableData.pagination" :sort="tableData.ordering" :items="tableData.rows"
                   :busy="tableData.loading" :fields="tableFields" show-toggle-columns :requiresMinHeight="true"
                   :search="tableData.search" export-vuex-module-name="dpv"
                   export-permission-name="view_auction" :export-attributes="exportAttributes"
                   :enable-simple-search="false" @paramsChanged="getTableData"
                   filter-toggle-string="dpv-table"
                   :extra-data="extraData" @update-toggle-columns="updateToggleColumns"
                   :enable-frontend-export="true"
                   @on-column-save="updateToggleColumns">
      <template v-slot:search-form-placeholder>
        <search-collapse table-id="dpv-table">
          <dpv-search/>
        </search-collapse>
      </template>
      <template slot="cell(sale_number)" slot-scope="{item: dpv}">
        {{ dpv.auction_number }}
      </template>
      <template slot="cell(sale_start_date)" slot-scope="{item: dpv}">
        {{ dpv.auction_start_date | toDateTime }}
        <span v-if="dpv.auction_end_date"> - {{ dpv.auction_end_date | toDateTime }}</span>
      </template>
      <template slot="cell(buyer_number)" slot-scope="{item: dpv}">
        {{ dpv.buyer_number }}
      </template>
      <template slot="cell(buyer_full_name)" slot-scope="{item: dpv}">
        {{ dpv.buyer_full_name }}
      </template>
      <template slot="cell(invoice_number)" slot-scope="{item: dpv}">
        <a href="#" @click.prevent="viewInvoice(dpv)">{{ dpv.invoice_number }}</a>
      </template>
      <template slot="cell(dpv_no)" slot-scope="{item: dpv}">
        <a href="#" @click.prevent="viewDetails(dpv.uuid)">{{ dpv.dpv_no }}</a>
      </template>
      <template slot="cell(total_hammer)" slot-scope="{item: dpv}">
        {{ dpv.total_hammer }}
      </template>
      <template slot="cell(description)" slot-scope="{item: dpv}">
        <span @click="viewDescription(dpv.description)">{{ dpv.description }}</span>
      </template>
      <template slot="cell(pdf_path)" slot-scope="{item: dpv}">
        <b-button @click="onViewPdfClick(dpv)" variant="primary" size="sm" class="mr-2">
          <font-awesome-icon icon="download"/>
        </b-button>
      </template>
      <template slot="cell(created_at)" slot-scope="{item: dpv}">
        {{ dpv.created_at | toDateTime }}
      </template>
    </artisio-table>
    <DpvLotsModal/>
    <DpvDescriptionModal/>
  </div>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import ArtisioTable from "@/core/components/ams-table/ArtisioTable";
  import SearchCollapse from "@/core/components/search-collapse/SearchCollapse";
  import i18n from "@/shared/i18n";
  import DpvSearch from "./DpvSearch";
  import DpvService from "./DpvService";
  import {DPV_TABLE_NAME} from "../constants";
  import DpvLotsModal from "./DpvLotsModal";
  import DpvDescriptionModal from "./DpvDescriptionModal";
  import cloneDeep from "lodash/cloneDeep";
  import {ON_PDF_MODAL_OPEN, ON_PDF_MODAL_DATA_RETRIEVE, ON_PDF_MODAL_CLOSE} from "@/plugins/event-bus/event-bus-names";
  import InvoicesService from "@/modules/invoices/InvoicesService";

  const {mapState, mapActions} = createNamespacedHelpers('dpv/dpv');
  const {mapActions: lotsMapActions} = createNamespacedHelpers('dpv/lots');

  export default {
    name: "DpvTable",
    components: {SearchCollapse, ArtisioTable, DpvSearch, DpvLotsModal, DpvDescriptionModal},
    props: {
      exportAttributes: {
        type: Object,
        default: () => {
          return {
            sale_number: i18n.t('Sale Number'),
            sale_start_date: i18n.t('Sale Start Date'),
            sale_end_date: i18n.t('Sale End Date'),
            buyer_number: i18n.t('Buyer Number'),
            buyer_full_name: i18n.t('Buyer Full Name'),
            invoice_number: i18n.t('Invoice Number'),
            dpv_no: i18n.t('DPV Number'),
            total_hammer: i18n.t('Total Hammer'),
            description: i18n.t('DPV Description'),
            created_at: i18n.t('Created At Date'),
            progress: null
          }
        }
      }
    },
    computed: {
      ...mapState({
        tableData: state => state.tableData[state.tableSource],
        toggleColumns: state => state.tableData[state.tableSource].toggleColumns,
        tableSource: state => state.tableSource,
        descriptionModal: state => state.descriptionModal
      }),
      tableFields() {
        return cloneDeep(this.toggleColumns.fields).filter(field => field.isVisible);
      },
      extraData() {
        return {
          ...this.toggleColumns,
          tableName: `${DPV_TABLE_NAME}.${this.tableSource}`
        }
      }
    },
    methods: {
      ...mapActions(['getTableData', 'updateToggleColumns', 'showModal', 'showDescriptionModal']),
      ...lotsMapActions(['updateDpvUuid']),
      async onViewPdfClick(dpv) {
        await DpvService.getDpvPdfUrl(this, dpv.uuid);
      },
      viewDetails(dpvUuid) {
        this.updateDpvUuid(dpvUuid);
        this.showModal();
      },
      viewDescription(description) {
        this.showDescriptionModal(description);
      },
      async viewInvoice(dpv) {
        this.$evBus.$emit(ON_PDF_MODAL_OPEN, {
          title: this.$i18n.t(`Invoice {invoice_no}`, {invoice_no: dpv.invoice_number})
        });
        let pdfDataResponse = await InvoicesService.getInvoicePdfData(dpv.invoice_uuid);
        if (pdfDataResponse.success) {
          this.$evBus.$emit(ON_PDF_MODAL_DATA_RETRIEVE, {
            ...pdfDataResponse.body,
            filename: this.$i18n.t(`Invoice {invoice_no}`, {invoice_no: dpv.invoice_number})
          });
        } else {
          let msg = this.$i18n.t("An error occurred when trying to fetch invoice PDF");
          if (pdfDataResponse.body && pdfDataResponse.body.non_field_errors) {
            msg = pdfDataResponse.body.non_field_errors;
          }
          this.$errorDialog(msg);
          this.$evBus.$emit(ON_PDF_MODAL_CLOSE);
        }
      }
    },
    mounted() {
      this.getTableData();
    }
  }
</script>

<style lang="scss" scoped>
  /deep/ {
    .artisio-table {
      .dpv-description-col {
        max-width: 200px;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }
    }
  }
</style>
